import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  Bot,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  Gauge,
  GraduationCap,
  Loader2,
  Lock,
  LogOut,
  RotateCcw,
  Share2,
  ShieldCheck,
  UploadCloud,
  Users,
} from 'lucide-react';
import { DEFAULT_PUBLIC_AGENT_SLUG } from '../utils/agentRouting';
import { sanitizeWorkspaceSlug, setupOrAdminAuthHeaders } from '../utils/workspace';
import { AvatarProfile, useAdminApi } from '../hooks/useAdminApi';
import { buildAvatarLaunchUrl, getAvatarPlaceholderCount, isSallyAvatar } from '../utils/avatarProfiles';
import AvatarShareDialog from '../components/customer/AvatarShareDialog';

interface CustomerDashboardPageProps {
  isDarkMode: boolean;
}

interface PublicAgentConfig {
  customerSlug: string;
  agentSlug: string;
  displayName: string;
  companyName: string;
  branding?: {
    logoUrl?: string;
    iconUrl?: string;
  };
  sources?: {
    hasProfile?: boolean;
    hasScrapedPages?: boolean;
    hasAgentConfig?: boolean;
  };
}

interface WorkspaceProfile {
  workspaceSlug?: string;
  website?: string;
  productWebsite?: string;
  lastScrapedAt?: string | null;
  profile_sections?: {
    company_name?: string;
    agent_name?: string;
  };
  effectiveAgentName?: string;
}

interface BehaviorDraftResponse {
  source?: 'saved' | 'draft';
  modifierText?: string;
  draftText?: string;
  updatedAt?: string | null;
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';
const SALLY_AVATAR_PORTRAIT_URL = '/sally-avatar-portrait.jpg';
const SETUP_WIZARD_COMPLETED_PREFIX = 'setup-wizard-completed:';
const SETUP_WIZARD_VISITED_PREFIX = 'setup-wizard-visited:';
const SETUP_WIZARD_ACTIVE_AGENT_PREFIX = 'setup-wizard-active-agent:';

const asTrimmedString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const SALLY_FALLBACK_PROFILE: AvatarProfile = {
  id: 'sally',
  slug: 'sally',
  name: 'Sally',
  description: 'Qualifiziert Leads, beantwortet Angebotsfragen und führt Besucher zum nächsten Schritt.',
  role_label: 'Sales & Marketing',
  provider_avatar_id: '',
  portrait_url: SALLY_AVATAR_PORTRAIT_URL,
  system_prompt: '',
  behavior_prompt: '',
  status: 'published',
  is_system: true,
  version: 1,
  assigned_customers: [],
};

const setupWizardCompletedKey = (customerSlug: string, agentSlug: string): string =>
  `${SETUP_WIZARD_COMPLETED_PREFIX}${customerSlug}:${agentSlug}`;
const setupWizardVisitedKey = (customerSlug: string, agentSlug: string, step: string): string =>
  `${SETUP_WIZARD_VISITED_PREFIX}${customerSlug}:${agentSlug}:${step}`;

const CustomerDashboardPage: React.FC<CustomerDashboardPageProps> = ({ isDarkMode }) => {
  const { customerSlug: customerSlugParam } = useParams<{ customerSlug: string }>();
  const { adminSession, isAuthenticated, logout, getCustomerAvatars } = useAdminApi();
  const customerSlug = sanitizeWorkspaceSlug(customerSlugParam);
  const agentSlug = DEFAULT_PUBLIC_AGENT_SLUG;
  const initialSetupAgentSlug = sanitizeWorkspaceSlug(
    window.localStorage.getItem(`${SETUP_WIZARD_ACTIVE_AGENT_PREFIX}${customerSlug}`) || agentSlug,
  );
  const isSuperadmin = Boolean(adminSession?.is_superadmin)
    || adminSession?.role === 'superadmin'
    || adminSession?.platform_role === 'owner';
  const memberships = Array.isArray(adminSession?.customer_memberships) ? (adminSession?.customer_memberships || []) : [];
  const isCustomerUser = adminSession?.role === 'customeruser'
    || (memberships.some((membership) => membership.role === 'customeruser')
      && !memberships.some((membership) => membership.role === 'customeradmin'));
  const [agentConfig, setAgentConfig] = useState<PublicAgentConfig | null>(null);
  const [profile, setProfile] = useState<WorkspaceProfile | null>(null);
  const [scrapedCount, setScrapedCount] = useState<number | null>(null);
  const [behavior, setBehavior] = useState<BehaviorDraftResponse | null>(null);
  const [availableAvatars, setAvailableAvatars] = useState<AvatarProfile[]>([SALLY_FALLBACK_PROFILE]);
  const [avatarsLoading, setAvatarsLoading] = useState(false);
  const [avatarLoadWarning, setAvatarLoadWarning] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [setupWizardAgentSlug, setSetupWizardAgentSlug] = useState(initialSetupAgentSlug);
  const [setupWizardOpen, setSetupWizardOpen] = useState(() =>
    customerSlug !== 'synaworks'
      && window.localStorage.getItem(setupWizardCompletedKey(customerSlug, initialSetupAgentSlug)) !== 'true'
  );
  const [setupWizardCompleted, setSetupWizardCompleted] = useState(() =>
    window.localStorage.getItem(setupWizardCompletedKey(customerSlug, initialSetupAgentSlug)) === 'true'
  );
  const [setupWizardSuccessVisible, setSetupWizardSuccessVisible] = useState(false);
  const [sharingAvatar, setSharingAvatar] = useState<AvatarProfile | null>(null);
  const [visitedSetupSteps, setVisitedSetupSteps] = useState<Record<string, boolean>>(() => ({
    profile: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, initialSetupAgentSlug, 'profile')) === 'true',
    behavior: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, initialSetupAgentSlug, 'behavior')) === 'true',
    knowledgebase: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, initialSetupAgentSlug, 'knowledgebase')) === 'true',
  }));

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const encodedCustomer = encodeURIComponent(customerSlug);
        const scopedHeaders = setupOrAdminAuthHeaders(customerSlug);
        const [configResponse, profileResponse, scrapedResponse, behaviorResponse] = await Promise.all([
          fetch(`${API_BASE}/api/public/agents/${encodedCustomer}/${agentSlug}/config`, { headers: scopedHeaders }),
          fetch(`${API_BASE}/api/customer/workspace/${encodedCustomer}`, { headers: scopedHeaders }),
          fetch(`${API_BASE}/api/customer/workspace/${encodedCustomer}/scraped-content`, { headers: scopedHeaders }),
          fetch(`${API_BASE}/api/customer/workspace/${encodedCustomer}/behavior-draft`, { headers: scopedHeaders }),
        ]);

        if (!configResponse.ok && !profileResponse.ok) {
          throw new Error('dashboard_not_found');
        }

        const [configPayload, profilePayload, scrapedPayload, behaviorPayload] = await Promise.all([
          configResponse.ok ? configResponse.json() : Promise.resolve(null),
          profileResponse.ok ? profileResponse.json() : Promise.resolve(null),
          scrapedResponse.ok ? scrapedResponse.json() : Promise.resolve(null),
          behaviorResponse.ok ? behaviorResponse.json() : Promise.resolve(null),
        ]);

        if (cancelled) {
          return;
        }

        setAgentConfig(configPayload as PublicAgentConfig | null);
        setProfile(profilePayload as WorkspaceProfile | null);
        setScrapedCount(
          scrapedPayload && typeof scrapedPayload.count === 'number' ? scrapedPayload.count : null
        );
        setBehavior(behaviorPayload as BehaviorDraftResponse | null);
      } catch {
        if (!cancelled) {
          setError('Dashboard konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [agentSlug, customerSlug, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    const loadAvatars = async () => {
      if (!isAuthenticated) return;
      setAvatarsLoading(true);
      setAvatarLoadWarning('');
      const profiles = await getCustomerAvatars(customerSlug);
      if (cancelled) return;

      if (!profiles) {
        setAvailableAvatars([SALLY_FALLBACK_PROFILE]);
        setAvatarLoadWarning('Weitere Avatare konnten nicht geladen werden. Sally bleibt verfügbar.');
        setAvatarsLoading(false);
        return;
      }

      const published = profiles.filter(profile => profile.status === 'published');
      const sally = published.find(profile => profile.slug === 'sally' || profile.is_system);
      const custom = published.filter(profile => profile !== sally && profile.slug !== 'sally');
      setAvailableAvatars([sally || SALLY_FALLBACK_PROFILE, ...custom]);
      setAvatarsLoading(false);
    };

    void loadAvatars();
    return () => { cancelled = true; };
  }, [customerSlug, getCustomerAvatars, isAuthenticated]);

  useEffect(() => {
    const nextAgent = sanitizeWorkspaceSlug(
      window.localStorage.getItem(`${SETUP_WIZARD_ACTIVE_AGENT_PREFIX}${customerSlug}`) || agentSlug,
    );
    setSetupWizardAgentSlug(nextAgent);
    const isComplete = window.localStorage.getItem(setupWizardCompletedKey(customerSlug, nextAgent)) === 'true';
    setSetupWizardCompleted(isComplete);
    setSetupWizardOpen(customerSlug !== 'synaworks' && !isComplete);
    setSetupWizardSuccessVisible(false);
    setVisitedSetupSteps({
      profile: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, nextAgent, 'profile')) === 'true',
      behavior: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, nextAgent, 'behavior')) === 'true',
      knowledgebase: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, nextAgent, 'knowledgebase')) === 'true',
    });
  }, [agentSlug, customerSlug]);

  const dashboardData = useMemo(() => {
    const sections = profile?.profile_sections ?? {};
    const companyName = asTrimmedString(agentConfig?.companyName)
      || asTrimmedString(sections.company_name)
      || customerSlug;
    const agentName = asTrimmedString(agentConfig?.displayName)
      || asTrimmedString(sections.agent_name)
      || asTrimmedString(profile?.effectiveAgentName)
      || 'Sally';
    const logoUrl = asTrimmedString(agentConfig?.branding?.logoUrl)
      || asTrimmedString(agentConfig?.branding?.iconUrl);
    const hasScrape = Boolean(agentConfig?.sources?.hasScrapedPages) || (scrapedCount ?? 0) > 0;
    const hasProfile = Boolean(agentConfig?.sources?.hasProfile) || Boolean(profile);
    const promptApproved = behavior?.source === 'saved' && asTrimmedString(behavior.modifierText).length > 0;

    return {
      companyName,
      agentName,
      logoUrl,
      hasScrape,
      hasProfile,
      promptApproved,
      website: asTrimmedString(profile?.website),
      lastScrapedAt: profile?.lastScrapedAt ?? null,
    };
  }, [agentConfig, behavior, customerSlug, profile, scrapedCount]);

  const pageClasses = isDarkMode
    ? 'bg-slate-950 text-slate-50'
    : 'bg-slate-100 text-slate-950';
  const panelClasses = isDarkMode
    ? 'border-white/10 bg-slate-900 text-slate-100'
    : 'border-slate-200 bg-white text-slate-950';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-500';
  const subtlePanelClasses = isDarkMode
    ? 'border-white/10 bg-slate-950/60'
    : 'border-slate-200 bg-slate-50';
  const previewBubbleClasses = isDarkMode
    ? 'border-white/10 bg-slate-800 text-slate-100 shadow-[0_14px_30px_rgba(15,23,42,0.28)]'
    : 'border-slate-200 bg-white text-slate-700 shadow-[0_14px_30px_rgba(15,23,42,0.10)]';
  const sallyCardClasses = isDarkMode
    ? 'border-blue-400/30 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/55 text-slate-50'
    : 'border-blue-200 bg-gradient-to-br from-white via-white to-blue-50 text-slate-950';
  const isSynaworksCustomer = customerSlug === 'synaworks';
  const showSetupWizard = setupWizardOpen && !setupWizardCompleted;
  const setupWizardAvatar = availableAvatars.find((avatar) => (avatar.agent_slug || avatar.slug) === setupWizardAgentSlug);
  const setupWizardAgentName = setupWizardAvatar?.name || (setupWizardAgentSlug === 'sally' ? dashboardData.agentName : setupWizardAgentSlug);
  const setupRescrapeUrl = `/setup?mode=rescrape&customer=${encodeURIComponent(customerSlug)}`
    + `&workspace=${encodeURIComponent('main')}&agent=${encodeURIComponent(setupWizardAgentSlug)}`
    + `&redirect=${encodeURIComponent(`/dashboard/${customerSlug}`)}`;
  const setupWizardSteps = [
    {
      id: 'scraping',
      stepLabel: 'Schritt 1',
      label: 'Scraping',
      detail: scrapedCount !== null ? `${scrapedCount} Seiten erkannt` : 'Website analysieren',
      href: setupRescrapeUrl,
      complete: dashboardData.hasScrape,
      icon: <ExternalLink className="h-4 w-4" />,
    },
    {
      id: 'profile',
      stepLabel: 'Schritt 2',
      label: 'Website Profil',
      detail: 'Editierbarer Scraping-Text',
      href: `/setup/profile/${customerSlug}/main/${setupWizardAgentSlug}`,
      complete: dashboardData.hasProfile || visitedSetupSteps.profile,
      icon: <FileText className="h-4 w-4" />,
    },
    {
      id: 'behavior',
      stepLabel: 'Schritt 3',
      label: 'Verhalten',
      detail: 'Prompt prüfen und freigeben',
      href: `/setup/prompt/${customerSlug}/main/${setupWizardAgentSlug}`,
      complete: (setupWizardAgentSlug === agentSlug && dashboardData.promptApproved) || visitedSetupSteps.behavior,
      icon: <FileText className="h-4 w-4" />,
    },
    {
      id: 'knowledgebase',
      stepLabel: 'Schritt 4',
      label: 'Knowledgebase',
      detail: 'Optionale Dokumente hochladen',
      href: `/admin/${customerSlug}/${setupWizardAgentSlug}?tab=knowledgebase`,
      complete: visitedSetupSteps.knowledgebase,
      icon: <UploadCloud className="h-4 w-4" />,
    },
  ];
  const setupWizardReady = setupWizardSteps.every((step) => step.complete);
  const placeholderAvatars = [
    {
      key: 'it-positioning',
      title: 'IT-Positionierung intern',
      description: 'Macht Leistungen, Services und Nutzen der IT für Fachbereiche verständlich.',
      icon: <Building2 className="h-6 w-6 text-slate-500" />,
    },
    {
      key: 'key-user',
      title: 'Key-User Experte',
      description: 'Beantwortet wiederkehrende Anwenderfragen und entlastet interne Expertenteams.',
      icon: <Users className="h-6 w-6 text-slate-500" />,
    },
    {
      key: 'trainer',
      title: 'Trainer - Prozesse & Rollen',
      description: 'Unterstützt Onboarding, Enablement und Prozesswissen im Gespräch.',
      icon: <GraduationCap className="h-6 w-6 text-slate-500" />,
    },
  ].slice(0, getAvatarPlaceholderCount(availableAvatars.length));

  const handleLogout = () => {
    logout();
    window.location.assign('/');
  };

  const markSetupStepVisited = (stepId: string) => {
    if (stepId === 'scraping') {
      return;
    }
    window.localStorage.setItem(setupWizardVisitedKey(customerSlug, setupWizardAgentSlug, stepId), 'true');
    setVisitedSetupSteps((current) => ({ ...current, [stepId]: true }));
  };

  const handleCompleteSetupWizard = () => {
    window.localStorage.setItem(setupWizardCompletedKey(customerSlug, setupWizardAgentSlug), 'true');
    setSetupWizardCompleted(true);
    setSetupWizardSuccessVisible(true);
  };

  const handleReopenSetupWizard = (nextAgentSlug: string) => {
    const safeAgentSlug = sanitizeWorkspaceSlug(nextAgentSlug);
    if (customerSlug === 'synaworks' && safeAgentSlug === 'sally') return;
    window.localStorage.setItem(`${SETUP_WIZARD_ACTIVE_AGENT_PREFIX}${customerSlug}`, safeAgentSlug);
    window.localStorage.removeItem(setupWizardCompletedKey(customerSlug, safeAgentSlug));
    setSetupWizardAgentSlug(safeAgentSlug);
    setSetupWizardCompleted(false);
    setVisitedSetupSteps({
      profile: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, safeAgentSlug, 'profile')) === 'true',
      behavior: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, safeAgentSlug, 'behavior')) === 'true',
      knowledgebase: window.localStorage.getItem(setupWizardVisitedKey(customerSlug, safeAgentSlug, 'knowledgebase')) === 'true',
    });
    setSetupWizardOpen(true);
    setSetupWizardSuccessVisible(false);
    window.setTimeout(() => document.getElementById('avatar-setup-wizard')?.scrollIntoView({ behavior: 'smooth' }), 0);
  };

  if (!isAuthenticated) {
    const redirect = `/dashboard/${customerSlug}`;
    return (
      <Navigate
        to={`/login?customer=${encodeURIComponent(customerSlug)}&redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  if (isCustomerUser) {
    const firstGrant = adminSession?.agent_access?.find((grant) => grant.status === 'active');
    return firstGrant?.customer_slug && firstGrant?.agent_slug
      ? <Navigate to={`/agent/${firstGrant.customer_slug}/${firstGrant.agent_slug}?voice=live`} replace />
      : <Navigate to="/" replace />;
  }

  return (
    <main className={`min-h-screen px-4 py-6 sm:px-6 lg:px-8 ${pageClasses}`}>
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col gap-4 border-b border-slate-200/70 pb-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase ${mutedClasses}`}>Customer Dashboard</p>
            <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">{dashboardData.companyName}</h1>
            <p className={`mt-1 text-sm ${mutedClasses}`}>Customer-ID: {customerSlug}</p>
          </div>
          {isAuthenticated ? (
            <div className="flex flex-wrap gap-2">
              {isSuperadmin && isSynaworksCustomer ? (
                <Link
                  to="/superadmin"
                  className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-blue-400/40 text-blue-100 hover:bg-blue-950/50' : 'border-blue-200 text-blue-700 hover:bg-blue-50'}`}
                >
                  <ShieldCheck className="h-4 w-4" />
                  Superadmin
                </Link>
              ) : null}
              <button
                type="button"
                onClick={handleLogout}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-white/15 text-slate-200 hover:bg-white/10' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
              >
                <LogOut className="h-4 w-4" />
                Ausloggen
              </button>
            </div>
          ) : null}
        </header>

        {isLoading ? (
          <section className={`flex items-center gap-3 rounded-lg border p-5 ${panelClasses}`}>
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className={`text-sm ${mutedClasses}`}>Dashboard wird geladen...</span>
          </section>
        ) : null}

        {error ? (
          <section className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </section>
        ) : null}

        {showSetupWizard ? (
          <section id="avatar-setup-wizard" className={`rounded-lg border p-5 ${panelClasses}`}>
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className={`text-xs font-semibold uppercase ${mutedClasses}`}>Setup Wizard</p>
                <h2 className="mt-1 text-xl font-semibold">Setup Wizard für Avatar {setupWizardAgentName}</h2>
                <p className={`mt-1 text-sm ${mutedClasses}`}>
                  Gemeinsame Unternehmensdaten werden geprüft; Behaviour Prompt und Knowledgebase bleiben auf {setupWizardAgentName} begrenzt.
                </p>
              </div>
              {setupWizardReady ? (
                <button
                  type="button"
                  onClick={handleCompleteSetupWizard}
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500"
                >
                  Setup abschließen
                </button>
              ) : null}
            </div>

            <div className="grid gap-3 lg:grid-cols-4">
              {setupWizardSteps.map((item, index) => (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={() => markSetupStepVisited(item.id)}
                  className={`relative flex min-h-[132px] flex-col justify-between rounded-lg border p-4 transition hover:-translate-y-0.5 hover:shadow-md ${
                    item.complete
                      ? isDarkMode
                        ? 'border-emerald-400/35 bg-emerald-950/25'
                        : 'border-emerald-200 bg-emerald-50'
                      : isDarkMode
                        ? 'border-white/10 bg-slate-950/60 hover:border-blue-400/40'
                        : 'border-slate-200 bg-slate-50 hover:border-blue-200'
                  }`}
                >
                  {index < setupWizardSteps.length - 1 ? (
                    <span
                      className={`pointer-events-none absolute right-[-12px] top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 rotate-45 border-r border-t lg:block ${
                        item.complete
                          ? isDarkMode ? 'border-emerald-400/35 bg-emerald-950' : 'border-emerald-200 bg-emerald-50'
                          : isDarkMode ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'
                      }`}
                    />
                  ) : null}
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <p className={`text-xs font-semibold uppercase ${mutedClasses}`}>{item.stepLabel}</p>
                      <CheckCircle2 className={`h-4 w-4 ${item.complete ? 'text-emerald-500' : mutedClasses}`} />
                    </div>
                    <h3 className="mt-3 text-base font-semibold">{item.label}</h3>
                    <p className={`mt-2 text-sm leading-5 ${mutedClasses}`}>{item.detail}</p>
                  </div>
                  <span className={`mt-4 inline-flex items-center gap-2 text-xs font-semibold ${item.complete ? 'text-emerald-600' : isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                    {item.icon}
                    {item.complete ? 'Erledigt' : 'Öffnen'}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {setupWizardSuccessVisible ? (
          <section className={`rounded-lg border p-4 text-sm font-semibold ${isDarkMode ? 'border-emerald-400/30 bg-emerald-950/30 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            Setup Wizard erfolgreich abgeschlossen.
          </section>
        ) : null}

        <section className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Avatare</h2>
              <p className={`mt-1 text-sm ${mutedClasses}`}>Wähle den digitalen Experten für dein nächstes Gespräch.</p>
            </div>
            <div className={`flex items-center gap-2 text-xs ${mutedClasses}`} role={avatarsLoading ? 'status' : undefined}>
              {avatarsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
              {avatarsLoading ? 'Freigaben werden geladen…' : `${availableAvatars.length} verfügbar`}
            </div>
          </div>
          {avatarLoadWarning ? (
            <div className={`rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-amber-700/40 bg-amber-950/20 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              {avatarLoadWarning}
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            {availableAvatars.map((avatar) => {
              const isSally = isSallyAvatar(avatar);
              const title = isSally ? dashboardData.agentName : avatar.name;
              const cardAgentSlug = avatar.agent_slug || avatar.slug || agentSlug;
              const avatarUrl = buildAvatarLaunchUrl(customerSlug, avatar);
              return (
              <article
                key={avatar.id || avatar.slug}
                className={`group rounded-lg border p-5 ${sallyCardClasses}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="relative flex h-28 w-24 shrink-0 items-center justify-center">
                      <div className={`relative h-24 w-24 overflow-hidden rounded-lg border transition-transform duration-200 group-hover:scale-[1.03] ${isDarkMode ? 'border-white/10 bg-slate-800' : 'border-white bg-white'}`}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Bot className="h-8 w-8 text-blue-500" />
                        </div>
                        {avatar.portrait_url || isSally ? (
                          <img
                            src={avatar.portrait_url || SALLY_AVATAR_PORTRAIT_URL}
                            alt={`${title} Avatar`}
                            className="relative h-full w-full object-cover"
                            onError={event => { event.currentTarget.style.display = 'none'; }}
                          />
                        ) : null}
                        {dashboardData.logoUrl ? (
                          <img
                            src={dashboardData.logoUrl}
                            alt=""
                            className="absolute bottom-1 right-1 h-4 w-4 rounded bg-white object-contain p-0.5"
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <p className={`text-xs font-semibold uppercase ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                          {isSally ? 'Standard Avatar' : 'Freigeschaltet'}
                        </p>
                      </div>
                      <h3 className="truncate text-xl font-semibold">
                        {title}
                      </h3>
                      <p className={`mt-0.5 text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {avatar.role_label || (isSally ? 'Sales & Marketing' : 'Digitaler Experte')}
                      </p>
                      <p className={`mt-1 text-sm leading-6 ${mutedClasses}`}>{avatar.description || 'Unterstützt dich im persönlichen Live-Gespräch.'}</p>
                    </div>
                  </div>
                  <div className="hidden w-44 shrink-0 flex-col items-end gap-2 xl:flex">
                      <span className={`rounded-lg border px-3 py-2 text-xs leading-5 opacity-0 transition duration-200 group-hover:translate-y-0 group-hover:opacity-100 ${previewBubbleClasses}`}>
                        Hallo, wie kann ich heute unterstützen?
                      </span>
                      <span className={`rounded-lg border px-3 py-2 text-xs leading-5 opacity-0 transition delay-75 duration-200 group-hover:translate-y-0 group-hover:opacity-100 ${previewBubbleClasses}`}>
                        Starte das Gespräch direkt mit mir.
                      </span>
                  </div>
                </div>

                <div className={`mt-5 grid gap-2 ${isSally ? 'sm:grid-cols-3' : 'sm:grid-cols-2 xl:grid-cols-4'}`}>
                    <Link
                      to={avatarUrl}
                      className="sally-avatar-open-button inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Avatar öffnen
                    </Link>
                    <Link
                        to={`/admin/${customerSlug}/${cardAgentSlug}`}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-white/15 hover:bg-white/10' : 'border-slate-300 hover:bg-slate-100'}`}
                      >
                        <Gauge className="h-4 w-4" />
                        Admin
                      </Link>
                    {isSally && isSynaworksCustomer ? (
                      <span
                        title="Der bestehende Synaworks-Sally-Prompt ist vor Setup-Überschreibungen geschützt."
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${isDarkMode ? 'border-red-400/25 bg-red-950/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}
                      >
                        <Lock className="h-4 w-4" />
                        Setup geschützt
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleReopenSetupWizard(cardAgentSlug)}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-blue-400/35 text-blue-100 hover:bg-blue-950/40' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                        Setup
                      </button>
                    )}
                    {!isSally ? (
                      <button
                        type="button"
                        onClick={() => setSharingAvatar(avatar)}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-blue-400/30 text-blue-100 hover:bg-blue-950/35' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                      >
                        <Share2 className="h-4 w-4" />
                        Teilen
                      </button>
                    ) : null}
                </div>
              </article>
            );})}

            {placeholderAvatars.map(avatar => (
              <article key={avatar.key} className={`rounded-lg border p-5 ${panelClasses}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border ${subtlePanelClasses}`}>
                      {avatar.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold">{avatar.title}</h3>
                      <p className={`mt-1 text-sm leading-6 ${mutedClasses}`}>{avatar.description}</p>
                    </div>
                  </div>
                  <Lock className={`h-4 w-4 shrink-0 ${mutedClasses}`} />
                </div>
                <div className={`mt-5 rounded-lg border px-3 py-2 text-sm ${isDarkMode ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                  Nicht freigeschaltet
                </div>
              </article>
            ))}
          </div>
        </section>
        {sharingAvatar ? (
          <AvatarShareDialog
            isOpen
            onClose={() => setSharingAvatar(null)}
            isDarkMode={isDarkMode}
            customerSlug={customerSlug}
            workspaceSlug="main"
            agentSlug={sharingAvatar.agent_slug || sharingAvatar.slug}
            agentName={sharingAvatar.name}
            portraitUrl={sharingAvatar.portrait_url}
          />
        ) : null}
      </div>
    </main>
  );
};

export default CustomerDashboardPage;
